import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, ExternalLink, ShieldCheck, Landmark, ArrowRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { ClientShell, useClientUser } from "@/pages/client/ClientShell";

const DocCard = ({ title, number, path, testid, emptyTo, emptyLabel }) => (
  <div data-testid={testid} className="flex flex-col border border-slate-200 bg-white">
    <div className="flex items-center justify-between border-b border-slate-200 bg-[#F6F4FD] px-5 py-3">
      <p className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#6D28D9]"><FileText className="h-3.5 w-3.5" /> {title}</p>
      {number && <span data-testid={`${testid}-number`} className="font-mono2 text-xs text-slate-800">{number}</span>}
    </div>
    {path ? (
      <a href={fileUrl(path)} target="_blank" rel="noreferrer" data-testid={`${testid}-open`} className="group relative block h-48 overflow-hidden bg-slate-50">
        <img src={fileUrl(path)} alt={title} className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105" />
        <span className="absolute bottom-2 right-2 flex items-center gap-1 bg-white/90 px-2 py-1 font-mono2 text-[9px] uppercase tracking-[0.15em] text-[#6D28D9]"><ExternalLink className="h-3 w-3" /> Open</span>
      </a>
    ) : (
      <div className="flex h-48 flex-col items-center justify-center gap-3 px-6 text-center">
        <p className="text-sm text-slate-500">{emptyLabel}</p>
        <Link data-testid={`${testid}-cta`} to={emptyTo} className="inline-flex items-center gap-1.5 border border-[#6D28D9]/40 px-4 py-2 font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#6D28D9] hover:bg-violet-50">Add now <ArrowRight className="h-3 w-3" /></Link>
      </div>
    )}
  </div>
);

export default function ClientDocuments() {
  const user = useClientUser();
  const [d, setD] = useState(null);
  useEffect(() => { if (user) api.get("/client/documents").then((r) => setD(r.data)).catch(() => setD({ demat: { saved: false } })); }, [user]);
  if (!user) return null;

  return (
    <ClientShell user={user} title="My Documents">
      <p className="mt-2 flex items-center gap-2 text-sm text-slate-500"><ShieldCheck className="h-4 w-4 text-[#6D28D9]" /> Your PAN card, Aadhaar / ID and demat account details — stored privately, visible only to you and the desk.</p>
      {!d && <p data-testid="documents-loading" className="mt-8 font-mono2 text-xs uppercase tracking-[0.25em] text-slate-500">Loading documents…</p>}
      {d && (
        <div data-testid="documents-grid" className="mt-8 grid gap-5 md:grid-cols-2">
          <DocCard title="PAN card" number={d.pan?.number} path={d.pan?.doc_path} testid="doc-pan" emptyTo="/client/kyc" emptyLabel="PAN card not uploaded yet. Complete your KYC to add it." />
          <DocCard title={d.identity?.type ? `${d.identity.type} (ID proof)` : "Aadhaar / ID proof"} number={d.identity?.number} path={d.identity?.doc_path} testid="doc-id" emptyTo="/client/kyc" emptyLabel="Aadhaar / ID proof not uploaded yet. Complete your KYC to add it." />
          <div data-testid="doc-demat" className="border border-slate-200 bg-white md:col-span-2">
            <div className="flex items-center justify-between border-b border-slate-200 bg-[#F6F4FD] px-5 py-3">
              <p className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#6D28D9]"><Landmark className="h-3.5 w-3.5" /> Demat account details</p>
              <Link data-testid="doc-demat-manage" to="/client/demat" className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-500 hover:text-[#6D28D9]">Manage</Link>
            </div>
            {d.demat?.saved ? (
              <dl className="grid gap-px bg-slate-200 sm:grid-cols-3">
                {[["Depository", d.demat.depository], ["DP ID", d.demat.dp_id], ["Client ID", d.demat.client_id_masked], d.demat.bo_id_masked ? ["BO ID (16 digits)", d.demat.bo_id_masked] : null, ["Account holder", d.demat.holder_name || user.name]].filter(Boolean).map(([k, v]) => (
                  <div key={k} className="bg-white px-5 py-4"><dt className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500">{k}</dt><dd data-testid={`doc-demat-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="mt-1 font-mono2 text-sm text-slate-900">{v}</dd></div>
                ))}
              </dl>
            ) : (
              <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
                <p className="text-sm text-slate-500">No demat account saved yet.</p>
                <Link data-testid="doc-demat-cta" to="/client/demat" className="inline-flex items-center gap-1.5 border border-[#6D28D9]/40 px-4 py-2 font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#6D28D9] hover:bg-violet-50">Add demat details <ArrowRight className="h-3 w-3" /></Link>
              </div>
            )}
          </div>
        </div>
      )}
    </ClientShell>
  );
}
